/**
 * CART CORE V2 - SIMPLIFIED ARCHITECTURE
 * Single responsibility: Core cart state management
 * Target: 200 lines, 15KB
 */

class CartCoreV2 {
    constructor() {
        this.state = {
            items: [],
            itemCount: 0,
            totalPrice: 0,
            isLoading: false,
            isOpen: false
        };

        this.listeners = new Map();
        // Debounce maps for quantity scheduling
        this.qtyTimers = new Map();      // key -> timeout id
        this.pendingQty = new Map();     // key -> last desired qty
        // Optimistic line tracking
        this.optimisticItems = new Map();   // placeholderId -> record
        this.optimisticOrder = [];          // maintain FIFO resolution
        this.optimisticByIdentity = new Map(); // lineIdentity -> pending quantity (cents consistent with quantity)
        this.optimisticQtyByKey = new Map(); // key/id -> optimistic quantity (for existing lines)
        // Keys recently removed (short TTL) to prevent stale reappearance during fast add-after-remove
        this.recentlyRemovedKeys = new Map(); // key -> timestamp ms
        this.recentlyRemovedTTL = 1200; // ms
        this.authoritativeItems = [];
        this.displayOrder = []; // stable order of identities for UI (legacy)
        this.identityOrderIndex = new Map(); // identity -> first-seen index
        this.identityCounter = 0;
        // Monotonic mutation sequencing to avoid stale state overwrites
        this.seq = 0;                 // increases on each mutation intent
        this.latestAppliedSeq = 0;    // last seq that updated state from server
        // Abort controller for cart fetch reconciliation
        this.cartFetchController = null;
        // Render coalescing
        this._rafEmitId = null;
        this._pendingEmitState = null;
        // Reconciliation instrumentation (debug only)
        this.reconcileStats = { started: 0, completed: 0, lastSeq: null };
        this.init();
    }

    init() {
        this.debugLog('🚀 Cart Core V2 initializing...');

        try {
            this.loadCartState();
            this.setupEventListeners();
            // Listen for optimistic add to update totals immediately
            document.addEventListener('cart:adding', (e) => {
                try {
                    const detail = e.detail || {};
                    this.addOptimisticItem(detail);
                } catch (err) {
                    console.debug('cart:adding optimistic handling failed (non-blocking):', err);
                }
            });

            // Emit ready event
            document.dispatchEvent(new CustomEvent('cart-core-v2:ready'));
            this.setupGlobalAPI();

            this.debugLog('✅ Cart Core V2 ready');

        } catch (error) {
            console.error('❌ Cart Core V2 initialization failed:', error);
        }
    }

    debugLog(...args) {
        try {
            if (window && window.debugCart) console.log(...args);
        } catch (_) {}
    }

    sanitizeProperties(properties = {}) {
        const clean = {};
        if (!properties || typeof properties !== 'object') return clean;
        Object.keys(properties)
            .sort()
            .forEach((key) => {
                const value = properties[key];
                if (value === undefined || value === null || value === '') return;
                clean[key] = value;
            });
        return clean;
    }

    normalizeLineIdentity(variantId, properties = {}) {
        if (variantId === undefined || variantId === null) return null;
        const id = String(variantId);
        const cleanProps = this.sanitizeProperties(properties);
        try {
            return JSON.stringify({ id, properties: cleanProps });
        } catch (_) {
            return `${id}|${Object.keys(cleanProps).map((key) => `${key}:${cleanProps[key]}`).join('|')}`;
        }
    }

    getIdentityFromItem(item) {
        if (!item || typeof item !== 'object') return null;
        if (item.__identity) return item.__identity;
        const variantId = item.variant_id ?? item.id ?? item.key ?? null;
        const props = item.properties && typeof item.properties === 'object' ? item.properties : {};
        return this.normalizeLineIdentity(variantId, props);
    }

    extractUnitPrice(item) {
        if (!item) return 0;
        const qty = Math.max(1, parseInt(item.quantity) || 0);
        const finalLine = parseInt(item.final_line_price);
        if (!isNaN(finalLine) && qty > 0) return Math.round(finalLine / qty);
        const finalPrice = parseInt(item.final_price);
        if (!isNaN(finalPrice)) return finalPrice;
        const price = parseInt(item.price);
        return isNaN(price) ? 0 : price;
    }

    computeTotalsFromItems(items = []) {
        return items.reduce(
            (acc, item) => {
                const qty = parseInt(item.quantity) || 0;
                const unit = this.extractUnitPrice(item);
                acc.count += qty;
                acc.total += unit * qty;
                return acc;
            },
            { count: 0, total: 0 }
        );
    }

    applyOptimisticOverlayToItems(baseItems = []) {
        let items = Array.isArray(baseItems) ? baseItems.map((item) => ({ ...item, __identity: this.getIdentityFromItem(item) })) : [];

        // First apply quantity overrides by key to keep existing lines stable (no DOM reorder)
        if (this.optimisticQtyByKey && this.optimisticQtyByKey.size) {
            const adjusted = [];
            for (const it of items) {
                const key = it.key || it.id;
                if (this.optimisticQtyByKey.has(key)) {
                    const q = parseInt(this.optimisticQtyByKey.get(key));
                    if (!isNaN(q) && q <= 0) {
                        // Optimistically removed
                        continue;
                    } else if (!isNaN(q)) {
                        const unit = this.extractUnitPrice(it);
                        adjusted.push({
                            ...it,
                            quantity: q,
                            final_price: unit,
                            final_line_price: unit * q
                        });
                        continue;
                    }
                }
                adjusted.push(it);
            }
            items = adjusted;
        }

        if (!this.optimisticByIdentity || !this.optimisticByIdentity.size) {
            return items;
        }

        for (const [identity, aggregate] of this.optimisticByIdentity.entries()) {
            if (!aggregate || aggregate.quantity <= 0 || !identity) continue;

            const idx = items.findIndex((item) => item.__identity === identity);
            const unitPrice = aggregate.unitPrice || this.extractUnitPrice(aggregate.detail) || 0;

            if (idx > -1) {
                const current = items[idx];
                const baseQty = parseInt(current.quantity) || 0;
                const projectedQty = baseQty + aggregate.quantity;
                items[idx] = {
                    ...current,
                    quantity: projectedQty,
                    final_price: unitPrice,
                    final_line_price: unitPrice * projectedQty,
                    __optimistic: aggregate.quantity > 0,
                    __identity: identity
                };
            } else {
                const detail = aggregate.detail || {};
                const placeholderId = aggregate.placeholderIds && aggregate.placeholderIds[0]
                    ? aggregate.placeholderIds[0]
                    : `ph-${Date.now()}`;
                // Append to the end to preserve perceived order (avoid jumping)
                items.push({
                    key: detail.key || `optimistic-${placeholderId}`,
                    id: detail.variantId || detail.id || detail.key || `optimistic-${placeholderId}`,
                    variant_id: detail.variantId || detail.id,
                    title: detail.title || 'Adding to cart…',
                    variant_title: detail.variantTitle || '',
                    image: detail.image || detail.imageSrc || '',
                    properties: this.sanitizeProperties(detail.properties || {}),
                    quantity: aggregate.quantity,
                    price: unitPrice,
                    final_price: unitPrice,
                    final_line_price: unitPrice * aggregate.quantity,
                    __optimistic: true,
                    __placeholderId: placeholderId,
                    __identity: identity
                });
            }
        }

        return items;
    }

    normalizeAuthoritativeItem(item = {}, identity = null) {
        const clone = { ...item };
        const quantity = Math.max(0, parseInt(clone.quantity) || 0);
        const unitPrice = this.extractUnitPrice(clone);
        clone.quantity = quantity;
        clone.final_price = unitPrice;
        clone.final_line_price = unitPrice * quantity;
        clone.__identity = identity || this.getIdentityFromItem(clone);
        delete clone.__optimistic;
        delete clone.__placeholderId;
        return clone;
    }

    getPendingPlaceholderForIdentity(identity) {
        const aggregate = identity ? this.optimisticByIdentity.get(identity) : null;
        if (!aggregate || !Array.isArray(aggregate.placeholderIds) || !aggregate.placeholderIds.length) {
            return null;
        }
        return aggregate.placeholderIds[0];
    }

    getAuthoritativeBaseItems() {
        // Periodically purge expired entries
        this.purgeRecentlyRemoved();

        if (Array.isArray(this.authoritativeItems) && this.authoritativeItems.length) {
            return this.authoritativeItems
                .filter((it) => !this.isRecentlyRemoved(it.key || it.id))
                .map((item) => ({ ...item }));
        }

        const items = Array.isArray(this.state.items)
            ? this.state.items
                .filter((item) => !item.__optimistic)
                .map((item) => this.normalizeAuthoritativeItem(item, item.__identity))
                .filter((it) => !this.isRecentlyRemoved(it.key || it.id))
            : [];

        return items;
    }

    hasOptimisticPending() {
        return (this.optimisticItems && this.optimisticItems.size > 0)
            || (this.optimisticByIdentity && this.optimisticByIdentity.size > 0);
    }

    rebuildStateFromAuthoritative({ isLoading = this.state.isLoading } = {}) {
        const baseItems = this.getAuthoritativeBaseItems();
        const overlayItems = this.applyOptimisticOverlayToItems(baseItems);
        const orderedItems = this.applyStableDisplayOrder(overlayItems);
        const totals = this.computeTotalsFromItems(orderedItems);

        this.updateState({
            items: orderedItems,
            itemCount: totals.count,
            totalPrice: totals.total,
            isLoading
        });
    }

    getItemIdentityKey(item) {
        if (!item) return null;
        return item.__identity || item.key || item.id || null;
    }

    applyStableDisplayOrder(items = []) {
        try {
            // Track present identities
            const present = new Set();
            const withIdentity = [];
            const withoutIdentity = [];

            for (const it of items) {
                const id = this.getItemIdentityKey(it);
                if (!id) {
                    withoutIdentity.push(it);
                    continue;
                }
                present.add(id);
                if (!this.identityOrderIndex.has(id)) {
                    this.identityOrderIndex.set(id, this.identityCounter++);
                }
                withIdentity.push({ id, item: it, order: this.identityOrderIndex.get(id) });
            }

            // Drop stale identities so re-added items append to the end
            for (const stale of Array.from(this.identityOrderIndex.keys())) {
                if (!present.has(stale)) this.identityOrderIndex.delete(stale);
            }

            // Stable sort by first-seen order
            withIdentity.sort((a, b) => a.order - b.order);
            const ordered = withIdentity.map((e) => e.item).concat(withoutIdentity);

            // Keep legacy array for backward compat (not used for ordering anymore)
            this.displayOrder = withIdentity.map((e) => e.id);

            return ordered;
        } catch (e) {
            console.debug('applyStableDisplayOrder failed (non-blocking):', e);
            return items;
        }
    }

    fulfillOptimistic(identity, fulfilledQty = null, preferredPlaceholderId = null, { rebuild = true } = {}) {
        const cleared = [];
        let remaining = typeof fulfilledQty === 'number' && fulfilledQty >= 0 ? fulfilledQty : null;

        const removeRecord = (placeholderId) => {
            if (!placeholderId) return;
            const record = this.optimisticItems.get(placeholderId);
            this.optimisticItems.delete(placeholderId);
            this.optimisticOrder = this.optimisticOrder.filter((id) => id !== placeholderId);
            cleared.push(placeholderId);

            const recIdentity = record?.identity || this.normalizeLineIdentity(record?.detail?.variantId, record?.detail?.properties);
            const qty = parseInt(record?.quantity) || 0;

            if (recIdentity) {
                const aggregate = this.optimisticByIdentity.get(recIdentity);
                if (aggregate) {
                    aggregate.placeholderIds = (aggregate.placeholderIds || []).filter((id) => id !== placeholderId);
                    aggregate.quantity = Math.max(0, (aggregate.quantity || 0) - qty);
                    if (!aggregate.placeholderIds.length || aggregate.quantity <= 0) {
                        this.optimisticByIdentity.delete(recIdentity);
                    } else {
                        this.optimisticByIdentity.set(recIdentity, aggregate);
                    }
                }
            }

            if (remaining !== null) {
                remaining = Math.max(0, remaining - qty);
            }
        };

        if (preferredPlaceholderId) {
            removeRecord(preferredPlaceholderId);
        }

        if (identity) {
            const aggregate = this.optimisticByIdentity.get(identity);
            if (aggregate && Array.isArray(aggregate.placeholderIds)) {
                for (const placeholderId of [...aggregate.placeholderIds]) {
                    if (remaining !== null && remaining <= 0) break;
                    if (preferredPlaceholderId && placeholderId === preferredPlaceholderId) continue;
                    removeRecord(placeholderId);
                }
            }
        } else if (remaining === null || remaining > 0) {
            for (const placeholderId of [...this.optimisticOrder]) {
                if (remaining !== null && remaining <= 0) break;
                if (preferredPlaceholderId && placeholderId === preferredPlaceholderId) continue;
                removeRecord(placeholderId);
            }
        }

        if (rebuild) {
            this.rebuildStateFromAuthoritative({ isLoading: this.state.isLoading });
        }

        return cleared;
    }

    nextSeq() { this.seq += 1; return this.seq; }

    setupGlobalAPI() {
        // Ensure global functions are available immediately
        window.cartCoreV2 = this;

        // Legacy compatibility
        if (!window.updateCartCount) {
            window.updateCartCount = (count) => {
                this.updateCartCountBubble(count);
            };
        }

        // Add trackEvent method for compatibility
        this.trackEvent = (eventName, data) => {
            console.log('📊 V2 Track Event:', eventName, data);
            // In a real implementation, you'd send this to analytics
        };

        console.log('🌐 Cart Core V2 global API setup complete');
    }

    // =============================================================================
    // STATE MANAGEMENT
    // =============================================================================

    async loadCartState(applySeq = null, opts = {}) {
        try {
            // Capture request sequence at start to gate late responses
            const requestSeq = applySeq === null ? this.seq : applySeq;
            this.reconcileStats.started += 1;
            this.reconcileStats.lastSeq = requestSeq;
            this.debugLog('🔁 Reconciling cart', { seq: requestSeq, reason: opts?.reason || 'unspecified', started: this.reconcileStats.started });
            // Loading cart state from Shopify
            // Cancel any in-flight reconciliation; only latest should proceed
            try { this.cartFetchController?.abort(); } catch (_) {}
            this.cartFetchController = new AbortController();
            const response = await fetch('/cart.js', { signal: this.cartFetchController.signal, cache: 'no-store' });
            const cart = await response.json();

            // Cart data received successfully
            // Skip only truly stale responses (older than the last applied)
            if (requestSeq < this.latestAppliedSeq) {
                this.debugLog('⏭️ Skipping older cart state', { requestSeq, latestApplied: this.latestAppliedSeq, currentSeq: this.seq });
                return;
            }
            this.latestAppliedSeq = requestSeq;

            const normalizedItems = Array.isArray(cart.items)
                ? cart.items.map((item) => this.normalizeAuthoritativeItem(item))
                : [];

            this.authoritativeItems = normalizedItems.map((item) => ({ ...item }));
            this.rebuildStateFromAuthoritative({ isLoading: false });

            this.emit('cart:loaded', this.state);
            this.reconcileStats.completed += 1;
            this.debugLog('✅ Reconciled cart', { seq: requestSeq, completed: this.reconcileStats.completed });
            // Cart state loaded successfully
        } catch (error) {
            if (error?.name === 'AbortError') {
                console.debug('⏹️ Cart state fetch aborted in favor of newer request');
                return;
            }
            console.error('❌ Failed to load cart state:', error);
            this.emit('cart:error', error);
        }
    }

    updateState(newState) {
        const oldState = { ...this.state };
        this.state = { ...this.state, ...newState };

        // Ensure optimistic items map stays in sync after authoritative load
        if (
            newState &&
            Array.isArray(newState.items) &&
            !this.state.isLoading &&
            !this.hasOptimisticPending()
        ) {
            this.clearOptimisticTracking();
        }

        // Emit specific events for common changes
        if (oldState.itemCount !== this.state.itemCount) {
            this.emit('cart:count-changed', this.state.itemCount);
        }

        if (oldState.isOpen !== this.state.isOpen) {
            this.emit('cart:visibility-changed', this.state.isOpen);
        }

        this._scheduleStateChanged();
    }

    _scheduleStateChanged() {
        // Batch multiple updates in a single animation frame
        this._pendingEmitState = { ...this.state };
        if (this._rafEmitId !== null) return;
        this._rafEmitId = (typeof requestAnimationFrame === 'function'
            ? requestAnimationFrame
            : (fn) => setTimeout(fn, 16))(() => {
                const snapshot = this._pendingEmitState;
                this._rafEmitId = null;
                this._pendingEmitState = null;
                this.emit('cart:state-changed', snapshot);
            });
    }

    // =============================================================================
    // OPTIMISTIC SCHEDULING (QUANTITY/REMOVE)
    // =============================================================================

    scheduleQuantity(key, quantity) {
        try {
            // Record the desired quantity and debounce network call
            if (this.qtyTimers.has(key)) {
                clearTimeout(this.qtyTimers.get(key));
            }

            this.pendingQty.set(key, quantity);
            // If intent is a removal, mark key to suppress brief server echoes
            if (quantity <= 0) {
                this.markRecentlyRemoved(key);
            }
            // Apply optimistic UI immediately
            this.applyOptimisticQuantity(key, quantity);

            // Assign a mutation sequence NOW so older intents can't overwrite newer ones
            const seq = this.nextSeq();
            const timer = setTimeout(async () => {
                try {
                    const finalQty = this.pendingQty.get(key);
                    // Do not clear pending before we start the server update; keep UI stable
                    this.qtyTimers.delete(key);
                    // Use existing updateQuantity implementation (overridden by system V2)
                    await this.updateQuantity(key, finalQty, { seq });
                    // Clear pending once the request has been issued
                    this.pendingQty.delete(key);
                } catch (err) {
                    console.error('❌ Debounced quantity update failed:', err);
                    this.emit('cart:error', err);
                }
            }, 120);

            this.qtyTimers.set(key, timer);
        } catch (error) {
            console.error('❌ scheduleQuantity failed:', error);
        }
    }

    applyOptimisticQuantity(key, quantity) {
        try {
            this.setOptimisticQtyKey(key, quantity);
            // Rebuild from authoritative + overlays only; do not mutate authoritative snapshot
            this.rebuildStateFromAuthoritative({ isLoading: true });
        } catch (error) {
            console.error('❌ applyOptimisticQuantity failed:', error);
        }
    }

    setOptimisticQtyKey(key, quantity) {
        if (!key) return;
        if (quantity === null || quantity === undefined) return;
        this.optimisticQtyByKey.set(key, parseInt(quantity));
    }

    clearOptimisticQtyKey(key) {
        if (!key) return;
        this.optimisticQtyByKey.delete(key);
    }

    // =============================================================================
    // CART OPERATIONS
    // =============================================================================

    async addItem(variantId, quantity = 1, properties = {}, options = {}) {
        const normalizedProps = this.sanitizeProperties(properties);
        const lineIdentity = options.lineIdentity || this.normalizeLineIdentity(variantId, normalizedProps);
        const placeholderId = options.placeholderId || null;
        const seq = this.nextSeq();

        this.updateState({ isLoading: true });

        try {
            const formData = new FormData();
            formData.append('id', variantId);
            formData.append('quantity', quantity);

            Object.entries(normalizedProps).forEach(([key, value]) => {
                formData.append(`properties[${key}]`, value);
            });

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error(`Add to cart failed: ${response.status}`);
            }

            const payload = await response.json();
            const addedItem = Array.isArray(payload?.items) ? payload.items[0] : payload;
            const addedQty = parseInt(addedItem?.quantity) || quantity || 1;
            const serverIdentity = this.normalizeLineIdentity(
                addedItem?.key ?? addedItem?.id ?? variantId,
                this.sanitizeProperties(addedItem?.properties || normalizedProps)
            );

            let cleared = [];
            if (serverIdentity) {
                cleared = this.fulfillOptimistic(serverIdentity, addedQty, placeholderId || undefined);
            } else if (placeholderId) {
                cleared = this.resolveOptimisticItem(placeholderId) || [];
            }

            await this.loadCartState(seq);

            this.emit('cart:item-added', {
                variantId,
                quantity: addedQty,
                lineIdentity: serverIdentity,
                placeholderIds: cleared
            });

            // Drawer opening is handled by product-form.js after addItem() resolves
            // This ensures cart state is fully loaded before drawer opens

            return true;
        } catch (error) {
            console.error('❌ Add to cart failed:', error);
            if (placeholderId) {
                this.resolveOptimisticItem(placeholderId, { suppressRebuild: false });
            } else if (lineIdentity) {
                this.fulfillOptimistic(lineIdentity, 0);
            }
            this.emit('cart:error', error);
            return false;
        } finally {
            this.updateState({ isLoading: false });
        }
    }

    async updateQuantity(key, quantity, opts = {}) {
        this.updateState({ isLoading: true });

        try {
            const response = await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: key, quantity })
            });

            if (!response.ok) {
                throw new Error(`Update quantity failed: ${response.status}`);
            }

            if (quantity <= 0) {
                this.markRecentlyRemoved(key);
            }
            await this.loadCartState(opts?.seq ?? null);
            this.emit('cart:quantity-updated', { key, quantity });

            return true;
        } catch (error) {
            console.error('❌ Update quantity failed:', error);
            this.emit('cart:error', error);
            return false;
        } finally {
            this.updateState({ isLoading: false });
        }
    }

    async removeItem(key) {
        return this.updateQuantity(key, 0);
    }

    // =============================================================================
    // DRAWER CONTROL
    // =============================================================================

    openDrawer() {
        console.log('🚪 Cart Core V2: Opening drawer...');
        // Ensure state snapshot is rebuilt with latest overlays and suppression before opening
        try { this.rebuildStateFromAuthoritative({ isLoading: this.state.isLoading }); } catch (_) {}
        this.updateState({ isOpen: true });
        this.emit('cart:drawer-opened');

        // Also try to directly open the drawer element if core connection fails
        const drawer = document.querySelector('cart-drawer-v2');
        if (drawer && drawer.open) {
            console.log('🚪 Also calling drawer.open() directly');
            drawer.open();
        }
    }

    openDrawerWhenReady() {
        const drawer = document.querySelector('cart-drawer-v2');
        if (drawer && drawer.cartCore) {
            // Drawer is connected and ready
            console.log('🚪 Drawer is ready, opening now');
            this.openDrawer();
        } else {
            // Drawer not ready yet, wait and retry
            console.log('⏳ Drawer not ready, waiting...');
            setTimeout(() => this.openDrawerWhenReady(), 100);
        }
    }

    // =============================================================================
    // RECENTLY REMOVED SUPPRESSION
    // =============================================================================
    markRecentlyRemoved(key) {
        try {
            if (!key) return;
            const k = String(key);
            this.recentlyRemovedKeys.set(k, Date.now());
            // Keep map bounded
            if (this.recentlyRemovedKeys.size > 100) this.purgeRecentlyRemoved();
        } catch (_) {}
    }

    isRecentlyRemoved(key) {
        if (!key) return false;
        const k = String(key);
        const ts = this.recentlyRemovedKeys.get(k);
        if (!ts) return false;
        const fresh = Date.now() - ts <= this.recentlyRemovedTTL;
        if (!fresh) this.recentlyRemovedKeys.delete(k);
        return fresh;
    }

    purgeRecentlyRemoved() {
        const now = Date.now();
        for (const [k, ts] of this.recentlyRemovedKeys.entries()) {
            if (now - ts > this.recentlyRemovedTTL) this.recentlyRemovedKeys.delete(k);
        }
    }

    closeDrawer() {
        this.updateState({ isOpen: false });
        this.emit('cart:drawer-closed');
    }

    toggleDrawer() {
        if (this.state.isOpen) {
            this.closeDrawer();
        } else {
            this.openDrawer();
        }
    }

    // =============================================================================
    // EVENT SYSTEM
    // =============================================================================

    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);

        // Return unsubscribe function
        return () => {
            const callbacks = this.listeners.get(event);
            if (callbacks) {
                const index = callbacks.indexOf(callback);
                if (index > -1) {
                    callbacks.splice(index, 1);
                }
            }
        };
    }

    emit(event, data) {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.forEach(callback => {
                try {
                    callback(data);
                } catch (error) {
                    console.error(`❌ Event callback error for ${event}:`, error);
                }
            });
        }
    }

    setupEventListeners() {
        // Update cart count in header when count changes
        this.on('cart:count-changed', (count) => {
            this.updateCartCountBubble(count);
        });

        // Clear optimistic placeholders on error to avoid ghost rows
        this.on('cart:error', () => {
            this.clearOptimisticItems();
        });
    }

    updateCartCountBubble(count) {
        const button = document.querySelector('[data-cart-button]');
        if (!button) {
            console.log('⚠️ Cart button not found for count update');
            return;
        }

        const bubble = button.querySelector('.cart-count-bubble');
        if (bubble) {
            bubble.textContent = count;
            bubble.style.display = count > 0 ? 'flex' : 'none';
            bubble.setAttribute('data-count', count);
            // Cart count updated
        }

        // Update aria-label
        const label = count === 1 ? '1 item in cart' : `${count} items in cart`;
        button.setAttribute('aria-label', label);

        // Also update any other cart count elements
        document.querySelectorAll('.cart-count').forEach(el => {
            el.textContent = count;
        });
    }

    // =============================================================================
    // PUBLIC API
    // =============================================================================

    getState() {
        return { ...this.state };
    }

    hasPending() {
        return this.pendingQty && this.pendingQty.size > 0;
    }

    getProjectedQuantity(key) {
        // If there is a pending debounced target, prefer that
        if (this.pendingQty && this.pendingQty.has(key)) {
            const q = parseInt(this.pendingQty.get(key));
            return isNaN(q) ? 0 : q;
        }
        // Otherwise read from current state
        const items = Array.isArray(this.state.items) ? this.state.items : [];
        const item = items.find(i => (i.key && i.key === key) || (!i.key && i.id === key));
        const q = item ? parseInt(item.quantity) : 0;
        return isNaN(q) ? 0 : q;
    }

    addOptimisticItem(detail = {}) {
        try {
            const placeholderId = detail.placeholderId || `ph-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            const key = `optimistic-${placeholderId}`;
            const quantity = Math.max(1, parseInt(detail.quantity) || 1);
            const price = parseInt(detail.priceCents);
            const rawVariantId = detail.variantId ?? detail.id;
            const identity = detail.lineIdentity || this.normalizeLineIdentity(rawVariantId, detail.properties || {});
            const unitPrice = !isNaN(price) ? price : this.extractUnitPrice(detail);

            const record = {
                placeholderId,
                key,
                identity,
                detail: {
                    ...detail,
                    key,
                    variantId: rawVariantId,
                    properties: this.sanitizeProperties(detail.properties || {})
                },
                quantity,
                unitPrice
            };

            this.optimisticItems.set(placeholderId, record);
            this.optimisticOrder.push(placeholderId);

            if (identity) {
                const aggregate = this.optimisticByIdentity.get(identity) || {
                    quantity: 0,
                    unitPrice: unitPrice,
                    detail: record.detail,
                    placeholderIds: []
                };
                aggregate.quantity += quantity;
                aggregate.unitPrice = unitPrice || aggregate.unitPrice;
                aggregate.detail = aggregate.detail || record.detail;
                aggregate.placeholderIds.push(placeholderId);
                this.optimisticByIdentity.set(identity, aggregate);
            }

            const baseItems = this.getAuthoritativeBaseItems();
            const overlayItems = this.applyOptimisticOverlayToItems(baseItems);
            const totals = this.computeTotalsFromItems(overlayItems);

            this.updateState({ items: overlayItems, itemCount: totals.count, totalPrice: totals.total, isLoading: true });
            return placeholderId;
        } catch (error) {
            console.debug('addOptimisticItem failed (non-blocking):', error);
            return null;
        }
    }

    resolveOptimisticItem(placeholderId = null) {
        try {
            const id = placeholderId || this.optimisticOrder.shift();
            if (!id) return [];

            const record = this.optimisticItems.get(id);
            if (!record) {
                this.optimisticItems.delete(id);
                this.optimisticOrder = this.optimisticOrder.filter((pid) => pid !== id);
                this.rebuildStateFromAuthoritative({ isLoading: this.state.isLoading });
                return [];
            }

            const identity = record.identity || this.normalizeLineIdentity(record.detail?.variantId, record.detail?.properties);
            return this.fulfillOptimistic(identity, record.quantity, id);
        } catch (error) {
            console.debug('resolveOptimisticItem failed (non-blocking):', error);
            return [];
        }
    }

    clearOptimisticItems() {
        try {
            if (!this.optimisticItems.size && !this.optimisticByIdentity.size) return;
            this.clearOptimisticTracking();
            this.rebuildStateFromAuthoritative({ isLoading: this.state.isLoading });
        } catch (error) {
            console.debug('clearOptimisticItems failed (non-blocking):', error);
        }
    }

    clearOptimisticTracking() {
        this.optimisticItems.clear();
        this.optimisticOrder.length = 0;
        this.optimisticByIdentity.clear();
    }

    isReady() {
        return this.state.items !== null;
    }

    destroy() {
        this.listeners.clear();
        console.log('🗑️ Cart Core V2 destroyed');
    }
}

// =============================================================================
// INITIALIZATION
// =============================================================================

// Always initialize (V2 is default now)
console.log('🚀 Initializing Cart Core V2...');
window.cartCoreV2 = new CartCoreV2();

// Backward compatibility
window.cartSystem = window.cartCoreV2;

console.log('📦 Cart Core V2 module loaded');